import React from "react";
import { useNavigate, Link } from "react-router-dom";
import { Logo } from "./Logo";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import { Button } from "./ui/button";
import { ShoppingBag, LogOut, LayoutDashboard, Store, Bike, Shield, MapPin, Clock } from "lucide-react";

const roleLink = {
  customer: { label: "Customer", path: "/app", icon: ShoppingBag },
  merchant: { label: "Merchant", path: "/merchant", icon: Store },
  rider: { label: "Rider", path: "/rider", icon: Bike },
  admin: { label: "Admin", path: "/admin", icon: Shield },
};

export default function TopBar({ showCart = false, onCartClick }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const cart = useCart();

  const roleCfg = user ? roleLink[user.role] : null;
  const Icon = roleCfg?.icon;

  return (
    <header
      className="sticky top-0 z-40 glass-strong border-b border-cyan-500/15"
      data-testid="top-bar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className="flex items-center gap-2" data-testid="home-link">
          <Logo size={30} />
        </Link>

        {user?.role === "customer" && (
          <div className="hidden md:flex items-center gap-4 text-sm text-slate-300">
            <div className="flex items-center gap-1.5">
              <MapPin size={14} className="text-cyan-400" />
              <span className="font-medium">Indiranagar</span>
            </div>
            <div className="h-4 w-px bg-cyan-500/20" />
            <div className="flex items-center gap-1.5">
              <Clock size={14} className="text-cyan-400" />
              <span className="font-medium">Delivery in <span className="text-cyan-400">~8 mins</span></span>
            </div>
          </div>
        )}

        <div className="flex items-center gap-2">
          {user && (
            <>
              {roleCfg && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="hidden sm:inline-flex text-slate-200 hover:text-white hover:bg-white/5"
                  onClick={() => navigate(roleCfg.path)}
                  data-testid="role-dashboard-btn"
                >
                  {Icon && <Icon size={16} className="mr-1.5" />}
                  {roleCfg.label}
                </Button>
              )}

              {showCart && (
                <button
                  onClick={onCartClick}
                  data-testid="cart-toggle-btn"
                  className="relative inline-flex items-center gap-1.5 px-3 py-2 rounded-full glass hover:ring-cyan transition"
                >
                  <ShoppingBag size={16} className="text-cyan-400" />
                  <span className="text-sm font-semibold text-white">Cart</span>
                  {cart.count > 0 && (
                    <span
                      className="absolute -top-1.5 -right-1.5 bg-cyan-400 text-black text-[10px] font-bold rounded-full h-5 min-w-5 px-1 inline-flex items-center justify-center shadow-[0_0_12px_rgba(0,240,255,0.6)]"
                      data-testid="cart-count-badge"
                    >
                      {cart.count}
                    </span>
                  )}
                </button>
              )}

              <Button
                variant="ghost"
                size="icon"
                onClick={async () => { await logout(); navigate("/"); }}
                className="text-slate-300 hover:text-white hover:bg-white/5"
                data-testid="logout-btn"
                title="Logout"
              >
                <LogOut size={18} />
              </Button>
            </>
          )}

          {!user && (
            <Button
              className="btn-neon rounded-full px-4 py-2 h-9"
              onClick={() => navigate("/login")}
              data-testid="login-nav-btn"
            >
              Sign in
            </Button>
          )}
        </div>
      </div>
    </header>
  );
}
